import { useApplicationStore } from '@/stores/application'

/**
 * Global middleware: load Firestore before entering routes that need it.
 *
 * Firestore is loaded on demand (see utils/firebase/lazyFirestore.ts) so that anonymous visitors
 * on the main public pages don't download it (~555 KB). Signed-in users always have it, because
 * the auth flow loads it before the profile. Anonymous visitors skip it only on the routes below,
 * which render from the server API and Typesense. Every other route waits for it here, so pages
 * that read $firebase.collections during setup keep working.
 *
 * Before adding a route: open it signed out and check that no firebase/firestore module is
 * requested and that the console has no "used before ... loaded" errors.
 */
const ANONYMOUS_FIRESTORE_FREE_ROUTES = new Set([
    'index',
    'praca',
    'mentori',
    'dobrovolnictvo',
    'vzdelavanie',
    'blog',
    'blog-slug',
    'orgs-id',
    'mentor-id'
])

export default defineNuxtRouteMiddleware(async (to) => {
    if (import.meta.server) return

    const { $firebase } = useNuxtApp()
    const isSignedIn = Boolean(useApplicationStore().userId)

    if (!isSignedIn && typeof to.name === 'string' && ANONYMOUS_FIRESTORE_FREE_ROUTES.has(to.name)) {
        return
    }

    try {
        await $firebase.ensureFirestore()
    } catch (error) {
        // Let the page render: parts that don't need Firestore still work, and Firestore calls
        // fail loudly on their own.
        console.error('[firestore middleware] Could not load Firestore', error)
    }
})
